import {
  feedbackKindLabels,
  feedbackKinds,
  feedbackStatusLabels,
  feedbackStatuses,
  type Feedback,
  type FeedbackKind,
  type FeedbackStatus,
} from '@nexo/types';
import { Avatar, Badge, Button, EmptyState, Select, Textarea, useToast } from '@nexo/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, Inbox, Lightbulb } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { feedbackStatusTone } from '../feedback/MyFeedback';
import { AdminPage, Pagination } from './AdminLayout';
import { errorMessage } from '../../lib/forms';

/** Query key prefix of every admin feedback list (also used for the "new" counter). */
export const adminFeedbackKey = ['admin', 'feedback'] as const;

/** New reports waiting for an admin (shown next to «Reportes» in the admin menu). */
export function useNewFeedbackCount() {
  return useQuery({
    queryKey: [...adminFeedbackKey, 'new-count'],
    queryFn: () => api.admin.feedback({ status: 'new' }),
    select: (page) => page.total,
  }).data;
}

/** Errors and ideas sent by people from the app. */
export function AdminFeedback() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<FeedbackStatus | ''>('new');
  const [kind, setKind] = useState<FeedbackKind | ''>('');
  const [page, setPage] = useState(1);
  const reports = useQuery({
    queryKey: [...adminFeedbackKey, status, kind, page],
    queryFn: () =>
      api.admin.feedback({ status: status || undefined, kind: kind || undefined, page }),
    placeholderData: keepPreviousData,
  });

  return (
    <AdminPage
      title={t('Reportes')}
      description={t(
        'Errores y mejoras que la gente envía desde la app. Al marcar uno como resuelto se avisa por email a quien lo envió.',
      )}
    >
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="w-44">
          <Select
            aria-label={t('Estado')}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={[
              { value: '' as const, label: t('Todos los estados') },
              ...feedbackStatuses.map((s) => ({ value: s, label: t(feedbackStatusLabels[s]) })),
            ]}
          />
        </div>
        <div className="w-40">
          <Select
            aria-label={t('Tipo')}
            value={kind}
            onChange={(v) => {
              setKind(v);
              setPage(1);
            }}
            options={[
              { value: '' as const, label: t('Errores y mejoras') },
              ...feedbackKinds.map((k) => ({ value: k, label: t(feedbackKindLabels[k]) })),
            ]}
          />
        </div>
      </div>

      {reports.data?.items.length === 0 ? (
        <EmptyState
          icon={<Inbox className="size-8" />}
          title={t('No hay reportes')}
          description={status === 'new' ? t('Todo revisado.') : t('Prueba con otros filtros.')}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {reports.data?.items.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </ul>
      )}
      {reports.data && (
        <Pagination
          page={reports.data.page}
          pageSize={reports.data.pageSize}
          total={reports.data.total}
          onPage={setPage}
        />
      )}
    </AdminPage>
  );
}

function ReportCard({ report }: { report: Feedback }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState(report.reply ?? '');
  const update = useMutation({
    mutationFn: (input: { status?: FeedbackStatus; reply?: string | null }) =>
      api.admin.updateFeedback(report.id, input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: adminFeedbackKey });
      if (input.reply !== undefined) toast({ title: t('Respuesta guardada'), tone: 'success' });
    },
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });
  const { context } = report;
  const where = [
    context.platform === 'desktop' ? t('App de escritorio') : context.platform ? t('Web') : null,
    context.appVersion && `v${context.appVersion}`,
    context.path,
  ].filter(Boolean);

  return (
    <li className="rounded-xl border border-border p-4">
      <div className="flex flex-wrap items-start gap-3">
        <span
          className={
            report.kind === 'bug'
              ? 'flex size-8 shrink-0 items-center justify-center rounded-lg bg-danger-soft text-danger'
              : 'flex size-8 shrink-0 items-center justify-center rounded-lg bg-warning/15 text-warning'
          }
          title={t(feedbackKindLabels[report.kind])}
        >
          {report.kind === 'bug' ? <Bug className="size-4" /> : <Lightbulb className="size-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium">{report.title}</p>
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
            {report.author ? (
              <>
                <Avatar
                  id={report.author.id}
                  name={report.author.displayName}
                  src={report.author.avatarUrl}
                  size="xs"
                />
                {report.author.displayName} · {report.author.email}
              </>
            ) : (
              t('Cuenta eliminada')
            )}
            <span>· {formatRelative(report.createdAt)}</span>
          </p>
        </div>
        <Badge tone={feedbackStatusTone[report.status]}>
          {t(feedbackStatusLabels[report.status])}
        </Badge>
        <div className="w-36">
          <Select
            aria-label={t('Cambiar estado')}
            value={report.status}
            disabled={update.isPending}
            onChange={(status) => update.mutate({ status })}
            size="sm"
            options={feedbackStatuses.map((s) => ({ value: s, label: t(feedbackStatusLabels[s]) }))}
          />
        </div>
      </div>
      <p className="mt-3 text-sm whitespace-pre-line">{report.description}</p>
      {where.length > 0 && (
        <p className="mt-2 text-xs text-subtle" title={context.userAgent}>
          {where.join(' · ')}
        </p>
      )}
      <form
        className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate({ reply: reply.trim() || null });
        }}
      >
        <Textarea
          aria-label={t('Respuesta para quien lo envió')}
          placeholder={t('Respuesta para quien lo envió (la verá en su perfil)…')}
          rows={2}
          maxLength={2000}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          className="flex-1"
        />
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          disabled={reply === (report.reply ?? '')}
          loading={update.isPending}
        >
          {t('Guardar respuesta')}
        </Button>
      </form>
    </li>
  );
}
